import type { MeditationPauseMultiplier } from "~/features/meditations/types";
import { formatApproximateLength } from "~/features/meditations/utils/meditation-length";

import { PickerScreen } from "./picker-screen";
import { SelectableCard } from "./selectable-card";

const PAUSE_OPTIONS: readonly {
    multiplier: MeditationPauseMultiplier;
    label: string;
    description: string;
}[] = [
    {
        multiplier: 1,
        label: "Standard (1×)",
        description:
            "The script's own timings. Good for a first listen or a quick check-in.",
    },
    {
        multiplier: 2,
        label: "Longer (2×)",
        description:
            "Every pause is twice as long. Choose this if you tend to feel rushed, or a part needs time to come forward.",
    },
    {
        multiplier: 3,
        label: "Longest (3×)",
        description:
            "Every pause is three times as long. Choose this when you already know this part and want to sit with it.",
    },
];

export function pauseMultiplierLabel(
    multiplier: MeditationPauseMultiplier,
): string {
    return (
        PAUSE_OPTIONS.find((option) => option.multiplier === multiplier)
            ?.label ?? "Standard (1×)"
    );
}

interface PausePickerScreenProps {
    selectedMultiplier: MeditationPauseMultiplier;
    // AIDEV-NOTE: EVO-2476 — the length used to come straight off `selectedMeditation`, which a
    // resource exercise never has, so an Academy practice offered three pacing choices with no
    // hint of what any of them cost. The overlay now resolves the length for either kind and
    // passes the resolver in; `practiceName` is what the line names, and both are optional
    // because a generative template's length is unknown until it is rendered.
    estimatedSecondsAt?: (
        multiplier: MeditationPauseMultiplier,
    ) => number | null;
    practiceName?: string;
    onSelect: (multiplier: MeditationPauseMultiplier) => void;
    onBack: () => void;
}

function PacingLength({
    label,
    practiceName,
}: {
    label: string | null;
    practiceName?: string;
}) {
    if (!label) return null;
    return (
        <span className="mt-3 block font-body text-sm text-stone-500">
            {practiceName ? `${label} for ${practiceName}` : label}
        </span>
    );
}

export function PausePickerScreen({
    selectedMultiplier,
    estimatedSecondsAt,
    practiceName,
    onSelect,
    onBack,
}: PausePickerScreenProps) {
    return (
        <PickerScreen
            title="Pacing"
            hint="How long the voice stays quiet after each prompt. You can pause the audio at any time as well."
            onBack={onBack}
            columns="three"
        >
            {PAUSE_OPTIONS.map((option) => (
                <SelectableCard
                    key={option.multiplier}
                    selected={selectedMultiplier === option.multiplier}
                    onSelect={() => onSelect(option.multiplier)}
                >
                    <span className="block font-display text-xl font-semibold leading-tight tracking-[-0.01em] text-brand-forest-900">
                        {option.label}
                    </span>
                    <span className="mt-2 block font-body text-base leading-relaxed text-stone-600">
                        {option.description}
                    </span>
                    <PacingLength
                        label={formatApproximateLength(
                            estimatedSecondsAt?.(option.multiplier),
                        )}
                        practiceName={practiceName}
                    />
                </SelectableCard>
            ))}
        </PickerScreen>
    );
}
